/**
 * sampleEvent — the one invented event every "try it" surface renders from.
 *
 * Why this exists (Dennis, Sept 20, 2026): a prospect does not care whether the
 * board they are looking at is live. They care that it looks like the product,
 * that it never rots, and that a QR code they can actually scan lands on the
 * scoring screen a player would see on the first tee. Pointing the marketing
 * page at a real event kept in the database was a chore with no owner — the
 * moment the round expired the hero fell back to a thin sample, and nobody was
 * told. So: fixed data, generated deterministically, rendered by the REAL
 * components wherever one exists (PublicLeaderboardTv takes this board as a
 * prop and knows nothing about where it came from).
 *
 * Everything here is invented. Every surface that shows it says so.
 *
 * Shapes follow spectatorBoardService.buildTeamBoard as of Sept 2026 — the
 * fields PublicLeaderboardTv, tvBuildRows and TeamBoardRows read. If the board
 * payload gains a field the renderers rely on, add it here too; the demo cannot
 * drift silently because it is rendered by the same code as production.
 */

// ---------------------------------------------------------------------------
// Course — 18 holes, par 72. Yardages are only shown on the scoring screen.
// ---------------------------------------------------------------------------
export const SAMPLE_COURSE = {
  name: 'Harbor Course',
  tee: 'White',
  holes: [
    { hole_number: 1,  par: 4, handicap_index: 9,  yards: 371 },
    { hole_number: 2,  par: 5, handicap_index: 3,  yards: 512 },
    { hole_number: 3,  par: 4, handicap_index: 13, yards: 342 },
    { hole_number: 4,  par: 3, handicap_index: 17, yards: 158 },
    { hole_number: 5,  par: 4, handicap_index: 1,  yards: 418 },
    { hole_number: 6,  par: 4, handicap_index: 11, yards: 366 },
    { hole_number: 7,  par: 3, handicap_index: 15, yards: 176 },
    { hole_number: 8,  par: 5, handicap_index: 5,  yards: 528 },
    { hole_number: 9,  par: 4, handicap_index: 7,  yards: 389 },
    { hole_number: 10, par: 4, handicap_index: 12, yards: 355 },
    { hole_number: 11, par: 3, handicap_index: 18, yards: 149 },
    { hole_number: 12, par: 4, handicap_index: 4,  yards: 402 },
    { hole_number: 13, par: 5, handicap_index: 10, yards: 495 },
    { hole_number: 14, par: 4, handicap_index: 2,  yards: 431 },
    { hole_number: 15, par: 3, handicap_index: 16, yards: 183 },
    { hole_number: 16, par: 4, handicap_index: 8,  yards: 377 },
    { hole_number: 17, par: 4, handicap_index: 14, yards: 348 },
    { hole_number: 18, par: 5, handicap_index: 6,  yards: 521 },
  ],
};
export const SAMPLE_PAR = SAMPLE_COURSE.holes.reduce((s, h) => s + h.par, 0);   // 72

// ---------------------------------------------------------------------------
// The field — 24 four-man teams in a captain's choice (scramble), gross.
// The first ten are the teams the /events page has carried since Sept 18, so
// the page keeps its character. Team 7 is "yours" on every surface: it is the
// card the sample QR belongs to, and it starts on 1A so the scoring screen
// opens on Hole 1 exactly as a scanned card would. Its members' initials match
// the printed card and cart sign on /events (T. Blake, M. Carroll, J. Hendrix, R. Ruiz).
// ---------------------------------------------------------------------------
const TEAMS = [
  { n: 1,  start: '10',  members: ['Marcus Aiken', 'Tom Boyd', 'Luis Nunez', 'Daniel Rhee'] },
  { n: 2,  start: '2',   members: ['Will Calhoun', 'Greg Meyer', 'Kofi Osei', 'Ray Vance'] },
  { n: 3,  start: '3',   members: ['Alan Greer', 'Ben Moss', 'Peter Ng', 'Rob Whitfield'] },
  { n: 4,  start: '16B', members: ['Ed Dunn', 'Nick Ferrara', 'Chidi Igwe', 'Sam Platt'] },
  { n: 5,  start: '7',   members: ['Hugh Baird', 'Jin Cho', 'Mark Levine', 'Tyler Sorrell'] },
  { n: 6,  start: '12',  members: ['Carl Ames', 'Pat Doyle', 'Ian Kerr', 'Steve Webb'] },
  { n: 7,  start: '1A',  members: ['Tom Blake', 'Mike Carroll', 'Jim Hendrix', 'Rob Ruiz'] },
  { n: 8,  start: '6',   members: ['Gary Bell', 'Emeka Okafor', 'Paul Shaw', 'Vince Tran'] },
  { n: 9,  start: '14A', members: ['Lou Duval', 'Chris Hart', 'Sung Kim', 'Bob Price'] },
  { n: 10, start: '1B',  members: ['Ray Diaz', 'Glen Ford', 'Ken Lowe', 'Cal Pinckney'] },
  { n: 11, start: '4',   members: ['Art Bennett', 'Joe Kowalski', 'Andre Rivers', 'Doug Stein'] },
  { n: 12, start: '5',   members: ['Neil Ashby', 'Frank Galloway', 'Omar Haddad', 'Ted Munro'] },
  { n: 13, start: '8',   members: ['Wes Barlow', 'Kyle Fitch', 'Ravi Nair', 'Jack Talbot'] },
  { n: 14, start: '9',   members: ['Dean Coker', 'Matt Lindqvist', 'Sean Quinn', 'Reggie Woods'] },
  { n: 15, start: '11',  members: ['Bill Ackerman', 'Ron Delgado', 'Scott Hale', 'Eli Rosen'] },
  { n: 16, start: '13',  members: ['Phil Abbott', 'Hank Garrity', 'Lee Marsh', 'Zach Yost'] },
  { n: 17, start: '15',  members: ['Nate Burke', 'Owen Frazier', 'Sal Moreno', 'Grant Tuttle'] },
  { n: 18, start: '17',  members: ['Curt Ellison', 'Jay Holt', 'Marco Pace', 'Todd Simms'] },
  { n: 19, start: '18',  members: ['Rich Cobb', 'Al Jenkins', 'Drew Mercer', 'Lonnie Ward'] },
  { n: 20, start: '10A', members: ['Bruce Farr', 'Cam Ito', 'Jim Norris', 'Walt Sykes'] },
  { n: 21, start: '2A',  members: ['Gus Alvarez', 'Ken Drake', 'Roy Ostrander', 'Vic Thorne'] },
  { n: 22, start: '6A',  members: ['Hal Brandt', 'Skip Landry', 'Mel Ochoa', 'Stan Voss'] },
  { n: 23, start: '12A', members: ['Ollie Crane', 'Fred Kessler', 'Milo Reyes', 'Wade Ziegler'] },
  { n: 24, start: '14B', members: ['Lyle Dawson', 'Russ Goff', 'Ike Pruitt', 'Earl Wynn'] },
];

// composeTeamLabel in spectatorBoardService: SURNAMES, A→Z, joined ' / ' — a
// first initial only to break a duplicate surname. The board must read the way
// the product reads ("Greer / Moss / Ng / Whitfield"), never "Team 3".
const surname = (full) => { const t = full.trim().split(/\s+/); return t[t.length - 1]; };
export const composeTeamLabel = (members) => {
  const parts = members.map((m) => ({ first: m.trim().split(/\s+/)[0], last: surname(m) }));
  const sorted = parts.slice().sort((a, b) => a.last.localeCompare(b.last));
  return sorted.map((p) => {
    const dup = sorted.filter((q) => q.last.toLowerCase() === p.last.toLowerCase()).length > 1;
    return dup ? `${p.first[0]}. ${p.last}` : p.last;
  }).join(' / ');
};

// ---------------------------------------------------------------------------
// Scores — deterministic. A tiny LCG seeded once, so every build, every visitor
// and every screenshot sees the same board. Change SEED and the whole field
// reshuffles; nothing else needs touching.
// ---------------------------------------------------------------------------
const SEED = 20260920;
const lcg = (seed) => { let s = seed % 2147483647; if (s <= 0) s += 2147483646; return () => { s = (s * 16807) % 2147483647; return s / 2147483647; }; };

// Team "quality" — how often a scramble makes birdie. Spread so the board has a
// clear leader, a tie or two, and a tail, without anyone shooting 55.
const QUALITY = [0.40, 0.44, 0.62, 0.36, 0.55, 0.30, 0.47, 0.51, 0.58, 0.45, 0.33, 0.38, 0.28, 0.42, 0.25, 0.49, 0.22, 0.31, 0.35, 0.53, 0.27, 0.20, 0.24, 0.39];
// Holes completed so far — a shotgun in progress, most teams 12–14 through,
// two teams not scoring live (they have started; they just aren't keying it).
const THRU =    [14, 13, 13, 12, 14, 13, 12, 12, 14, 13, 12, 13, 0, 14, 12, 13, 13, 0, 12, 14, 13, 12, 13, 12];

// Shotgun-aware hole order: the team's starting hole first, wrapping past 18.
export const holeOrderFrom = (startHole) => {
  const s = startHole >= 1 && startHole <= 18 ? startHole : 1;
  return Array.from({ length: 18 }, (_, i) => ((s - 1 + i) % 18) + 1);
};
const startHoleOf = (start) => parseInt(String(start).replace(/[^0-9]/g, ''), 10) || 1;

const scoreOne = (rnd, par, quality) => {
  const r = rnd();
  if (r < quality * 0.10) return par - 2;          // eagle — rare, and only on a good team
  if (r < quality) return par - 1;                  // birdie
  if (r < 0.96) return par;                         // par
  return par + 1;                                   // the odd bogey keeps it honest
};

// Build every team's hole-by-hole gross once, at module load.
const rnd = lcg(SEED);
const TEAM_SCORES = TEAMS.map((t, i) => {
  const order = holeOrderFrom(startHoleOf(t.start));
  const played = order.slice(0, THRU[i]);
  const scores = {};
  played.forEach((h) => { const hole = SAMPLE_COURSE.holes[h - 1]; scores[h] = scoreOne(rnd, hole.par, QUALITY[i]); });
  return scores;
});

const totalsOf = (scores) => {
  let gross = 0, par = 0, played = 0;
  Object.keys(scores).forEach((h) => { gross += scores[h]; par += SAMPLE_COURSE.holes[h - 1].par; played += 1; });
  return { gross, par, played, vsPar: gross - par };
};

// ---------------------------------------------------------------------------
// The board — the exact object PublicLeaderboardTv / TeamBoardRows consume.
// ---------------------------------------------------------------------------
const buildRows = () => {
  const rows = TEAMS.map((t, i) => {
    const scores = TEAM_SCORES[i];
    const tot = totalsOf(scores);
    const has = tot.played > 0;
    return {
      player_id: `sample-team-${t.n}`,                 // tvBuildRows keys rows on player_id — must be unique
      team_number: t.n,
      name: composeTeamLabel(t.members),
      members: t.members.map((m, j) => ({ player_id: `sample-${t.n}-${j}`, name: m })),
      start_text: `Start ${t.start}`,
      starting_hole: startHoleOf(t.start),
      group_label: String(t.start).replace(/[0-9]/g, '') || null,
      section: has ? 'live' : 'not_started',
      has_scores: has,
      thru: tot.played,
      gross_total: has ? tot.gross : null,
      gross_vs_par: has ? tot.vsPar : null,
      net_total: null,
      net_vs_par: null,
      team_handicap: null,
      handicap_source: null,
      short_handed: false,
      entry_flag: null,
      tied: false,
      tie_mark: null,
      tie_status: null,
      position_label: null,
      _scores: scores,
    };
  });

  // Rank the live section the way the server does for a gross scramble: vs par,
  // then further through, then team number. Ties share a T label.
  const live = rows.filter((r) => r.section === 'live').sort((a, b) =>
    (a.gross_vs_par - b.gross_vs_par) || (b.thru - a.thru) || (a.team_number - b.team_number));
  let pos = 0;
  live.forEach((r, i) => {
    if (i === 0 || r.gross_vs_par !== live[i - 1].gross_vs_par) pos = i + 1;
    const tied = live.filter((x) => x.gross_vs_par === r.gross_vs_par).length > 1;
    r.tied = tied;
    r.position_label = `${tied ? 'T' : ''}${pos}`;
  });
  // One team is a hole behind on entry — the amber THRU cell is a real product
  // feature and worth showing once.
  const amber = live.find((r) => r.thru === 12);
  if (amber) amber.entry_flag = 'amber';

  const rest = rows.filter((r) => r.section !== 'live').sort((a, b) => a.team_number - b.team_number);
  return [...live, ...rest];
};

export const SAMPLE_ROWS = buildRows();

export const SAMPLE_BOARD = {
  state: 'live',
  live: true,
  event_name: 'Lowcountry Charity Classic',
  course_name: SAMPLE_COURSE.name,
  scoring_mode: 'team',
  basis: 'gross',
  has_net: false,
  competition_hole_set: 'all18',
  hole_count: 18,
  through: Math.max(...SAMPLE_ROWS.map((r) => r.thru)),
  team_count: SAMPLE_ROWS.length,
  player_count: SAMPLE_ROWS.reduce((s, r) => s + r.members.length, 0),
  tie_break: 'usga',
  places_paid: 3,
  ties_pending: false,
  // No public_address on purpose: the footer's "Follow along" slot would name a
  // board that does not exist. The example label goes there instead.
  public_address: null,
  rows: SAMPLE_ROWS,
};

// ---------------------------------------------------------------------------
// "Your" team for the scoring screen — Team 7, starting on hole 1.
// ---------------------------------------------------------------------------
export const SAMPLE_TEAM_NUMBER = 7;
export const SAMPLE_TEAM = (() => {
  const t = TEAMS.find((x) => x.n === SAMPLE_TEAM_NUMBER);
  return {
    team_number: t.n,
    label: composeTeamLabel(t.members),
    members: t.members.map((m, j) => ({ player_id: `sample-${t.n}-${j}`, name: m })),
    starting_hole: startHoleOf(t.start),
    group_label: String(t.start).replace(/[0-9]/g, '') || '',
    event_name: SAMPLE_BOARD.event_name,
    course_name: SAMPLE_COURSE.name,
    scorer_name: t.members[0].split(' ')[0],
  };
})();

// ---------------------------------------------------------------------------
// Where the "try it" screens live. The QR on every marketing image encodes
// SAMPLE_SCORING_URL — absolute, because a phone camera has no idea what site
// the picture came from — and it is the SHORT domain on purpose: 23 characters
// is a version-2 QR (25 modules) where the long address is version 3 (29), so
// each module is ~15% bigger and the code scans off a monitor more reliably.
// server.js redirects ttogolf.com/try → /try/scoring; it is also typeable.
// ---------------------------------------------------------------------------
export const SAMPLE_SITE = 'https://teetimeorganizer.net';
export const SAMPLE_SCORING_PATH = '/try/scoring';
export const SAMPLE_BOARD_PATH = '/try/leaderboard';
export const SAMPLE_SCORING_URL = 'https://ttogolf.com/try';
export const SAMPLE_SCORING_SHORT = 'ttogolf.com/try';
export const SAMPLE_BOARD_URL = `${SAMPLE_SITE}${SAMPLE_BOARD_PATH}`;

// One sentence, used verbatim wherever the sample is shown, so the labelling is
// consistent and nobody has to decide the wording twice.
export const SAMPLE_LABEL = 'Example — invented teams and scores, shown exactly as the product shows them.';
